// ===================================================================
// KAIXO - useVersionCheck
// ===================================================================
// Detecta cuándo el servidor publica una nueva versión del frontend
// (bundle main.[hash].js distinto al que tenemos cargado) y dispara
// auto-reload para que el usuario reciba la actualización sin tener
// que reinstalar el APK.
//
// Cómo funciona:
//   1. Al cargar la app, capturamos el hash del bundle main.js que se
//      cargó (lo extraemos del <script src=".../main.[hash].js">).
//   2. Cada 60s pedimos /asset-manifest.json (lo genera CRA en el
//      build) con cache-busting. Si el hash de main.js no coincide,
//      hay versión nueva.
//   3. Mostramos banner "Actualizando KAIXO…" durante 1.5s para que el
//      usuario lo vea, despachamos un evento 'kaixo:before-reload-update'
//      por si algún componente quiere persistir borrador, y reload.
//
// Por qué es seguro:
//   - localStorage SOBREVIVE al reload → JWT, kaixo_user, ajustes intactos.
//   - sessionStorage también sobrevive (sólo se pierde al cerrar la tab/app).
//   - Sockets se reconectan al volver a montar la app.
//
// Si el WebView no está conectado a internet o /asset-manifest.json
// devuelve error, simplemente no hacemos nada — la app sigue funcionando.
// ===================================================================

import { useEffect, useState, useCallback, useRef } from 'react';

// 10 min entre comprobaciones automáticas. La detección rápida de
// versiones nuevas la cubre `visibilitychange` + `focus` + el listener
// de Capacitor `appStateChange` — es decir, en cuanto el usuario vuelve
// a la app comprobamos al instante. El interval solo cubre el caso de
// app abierta y activa en foreground sin parar — escenario muy raro y
// que, al ser polling continuo, multiplica /asset-manifest.json por
// número de dispositivos. Bajar de 30s a 10min divide la carga por 20.
const POLL_INTERVAL_MS = 10 * 60_000;
const FIRST_CHECK_DELAY_MS = 10_000;
const RELOAD_DELAY_MS = 1500;

function getCurrentMainHash() {
    // Buscamos el <script src> que cargó el bundle principal. Si la app
    // se sirve en modo dev (CRA dev server) los bundles no llevan hash —
    // en ese caso devolvemos null y el hook se desactiva solo.
    try {
        const scripts = document.querySelectorAll('script[src*="/static/js/main."]');
        for (const s of scripts) {
            const m = s.src.match(/main\.([a-z0-9]+)\.js/i);
            if (m) return m[1];
        }
    } catch {}
    return null;
}

async function fetchLatestMainHash() {
    try {
        // cache:'no-store' para que cualquier proxy/CDN intermedio no
        // sirva versión vieja. asset-manifest.json es muy ligero (~1KB).
        const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
        if (!res.ok) return null;
        const manifest = await res.json();
        const latestUrl = manifest?.files?.['main.js'] || '';
        const m = latestUrl.match(/main\.([a-z0-9]+)\.js/i);
        return m ? m[1] : null;
    } catch {
        return null;
    }
}

export default function useVersionCheck({ enabled = true } = {}) {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [reloading, setReloading] = useState(false);
    const initialHashRef = useRef(null);

    const triggerReload = useCallback(() => {
        if (reloading) return;
        setReloading(true);
        // Avisamos a quien quiera persistir borrador (chat input, forms, etc.)
        try { window.dispatchEvent(new CustomEvent('kaixo:before-reload-update')); } catch {}
        // localStorage y sessionStorage sobreviven — el JWT no se pierde.
        setTimeout(() => {
            try { window.location.reload(); } catch {}
        }, RELOAD_DELAY_MS);
    }, [reloading]);

    useEffect(() => {
        if (!enabled) return;
        if (!initialHashRef.current) {
            initialHashRef.current = getCurrentMainHash();
        }
        const initialHash = initialHashRef.current;
        // Sin hash inicial (modo dev) no podemos detectar cambios → off
        if (!initialHash) return;

        let cancelled = false;

        const check = async () => {
            if (cancelled) return;
            const latest = await fetchLatestMainHash();
            if (!latest || cancelled) return;
            if (latest !== initialHash) {
                setUpdateAvailable(true);
            }
        };

        const firstCheck = setTimeout(check, FIRST_CHECK_DELAY_MS);
        const interval = setInterval(check, POLL_INTERVAL_MS);

        // Si la pestaña vuelve a primer plano tras estar oculta, comprobamos
        // inmediatamente — escenario muy típico: usuario abre la app tras
        // varias horas, queremos darle la versión nueva sin esperar 30s.
        const onVisibility = () => {
            if (document.visibilityState === 'visible') check();
        };
        const onFocus = () => check();
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);

        // En Capacitor (Android/iOS nativo), visibilitychange no siempre
        // dispara al volver al foreground tras backgroundear la app —
        // el WebView pausa JS y los listeners DOM pueden no enterarse.
        // Usamos @capacitor/app que SÍ es fiable en ese escenario.
        let appHandle = null;
        try {
            // Solo si estamos en plataforma nativa (no en navegador puro)
            if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
                // eslint-disable-next-line global-require
                const { App } = require('@capacitor/app');
                App.addListener('appStateChange', (state) => {
                    if (state?.isActive) check();
                }).then(h => { appHandle = h; }).catch(() => {});
            }
        } catch {}

        return () => {
            cancelled = true;
            clearTimeout(firstCheck);
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
            try { appHandle?.remove?.(); } catch {}
        };
    }, [enabled]);

    // Cuando se detecta versión nueva → auto-reload tras delay del banner
    useEffect(() => {
        if (updateAvailable && !reloading) {
            triggerReload();
        }
    }, [updateAvailable, reloading, triggerReload]);

    return { updateAvailable, reloading };
}
